<script setup lang="ts">
import axios from 'axios'

import RewardAudienceTag from '@/views/reward/components/RewardAudienceTag.vue'
import LoyaltyRewardModal from '@/views/loyalty/components/LoyaltyRewardModal.vue'
import RewardTypeTag from '@/views/reward/components/RewardTypeTag.vue'

import { useRewardForm } from '@/components/rewards/compmosable/useRewardForm'

import type { Audience, LoyaltyReward } from '@/types/types'
import type { Store } from '@/views/contests/Components/types/global'

const props = withDefaults(
  defineProps<{
    stores: Store[]
    rewards: LoyaltyReward[]
    hasRewardSmsOption?: boolean
    hasCompatibleVoucherIntegration?: boolean
    pagination?: { currentPage: number; lastPage: number; total: number; pageSize?: number }
    isLoading?: boolean
  }>(),
  {
    hasRewardSmsOption: false,
    hasCompatibleVoucherIntegration: false,
    pagination: undefined,
    isLoading: false
  }
)

const emit = defineEmits<{
  (e: 'updateReward'): void
  (e: 'handleShowAll', value?: boolean): void
  (e: 'update:change-page', value: number): void
}>()

const { user, hasAllPartnersWithSegmentationSupport } = useLocalStorage()
const { associateLoyaltyProgramReward, fetchMerchantRewards } = useReward()
const { defaultReward, isRewardTypesReady, rewardTypesError } = useRewardForm()
const { getStoresByPermission } = useStore()
const { hasPermissionBySlug } = useAccess()
const { t } = useI18n()
const { formatNumber } = useNumber()
const route = useRoute()
const router = useRouter()
const notification = useNotification()

const storesWithPermission = computed(() => getStoresByPermission('reward.read'))

const isSelectExistingRewardModalOpen = ref(false)
const existingRewards = ref<LoyaltyReward[]>([])

const allCustomerLists = ref<Audience[]>([])
const isCreateEditRewardModalOpen = ref(false)
const isCancelDissociateReward = ref(false)
const isAddingFromSelection = ref(false)
const selectedReward: Ref<(LoyaltyReward & { includedSegmentIds: number[]; excludedSegmentIds: number[] }) | null> =
  ref(null)

const rewardList = computed(() => {
  return props.rewards.filter(
    (r) => r.storeIds.some((id) => props.stores.some((store) => store.id === id)) || r.storeIds.length === 0
  )
})

const tableColumns = {
  image: {
    label: '',
    headerClass: 'w-20! text-left'
  },
  type: {
    label: t('pages.reward.table.type'),
    headerClass: 'w-48! text-left'
  },
  name: {
    label: t('pages.reward.table.name'),
    headerClass: 'w-48! text-left'
  },
  points: {
    label: t('pages.reward.table.points'),
    headerClass: 'w-32!',
    numeric: true
  },
  segments: {
    label: t('pages.reward.table.segments'),
    headerClass: 'w-48! text-left'
  },
  stores: {
    label: t('pages.reward.table.stores'),
    headerClass: 'w-auto! text-left'
  }
}

const tableRows = computed(() => {
  if (!rewardList.value || rewardList.value.length === 0) {
    return []
  }

  return rewardList.value.map((reward) => ({
    id: reward.id,
    data: {
      reward,
      image: reward,
      type: reward,
      points: reward.points || 0,
      stores: reward.stores || [],
      name: reward.name || '',
      includedSegments: reward.includedSegments || [],
      excludedSegments: reward.excludedSegments || []
    }
  }))
})

const canUpdateReward = hasPermissionBySlug('reward.update')
const canCreateReward = hasPermissionBySlug('reward.create')

const openSelectExistingRewardModal = async () => {
  if (!canCreateReward) {
    notification.create('error', {
      text: t('pages.loyaltyPage.errors.createRewardPermission'),
      timeout: 5000
    })
  } else {
    isSelectExistingRewardModalOpen.value = true
    const all: LoyaltyReward[] = []
    let currentPage = 1
    let lastPage = 1
    do {
      const res = await fetchMerchantRewards(undefined, undefined, undefined, currentPage, 100)
      all.push(...(res.data || []))
      lastPage = res.meta?.last_page ?? 1
      currentPage++
    } while (currentPage <= lastPage)
    const associatedIds = new Set(rewardList.value.map((r) => r.id))
    existingRewards.value = all.filter((r) => !associatedIds.has(r.id))
  }
}

const closeSelectExistingRewardModal = () => {
  isSelectExistingRewardModalOpen.value = false
}

const handleCreateNewFromSelection = () => {
  isSelectExistingRewardModalOpen.value = false
  isCancelDissociateReward.value = false
  isAddingFromSelection.value = true
  if (!isRewardTypesReady.value || !defaultReward.value) {
    notification.create('error', {
      text: rewardTypesError.value || t('pages.reward.errors.fetchReward')
    })
    return
  }
  openCreateEditRewardModal(defaultReward.value)
}

const handleSelectExistingReward = async (rewardId: number) => {
  try {
    const currentReward = existingRewards.value.find((reward) => reward.id === rewardId)
    isAddingFromSelection.value = true

    if (currentReward?.usages?.includes('loyalty')) {
      isCancelDissociateReward.value = false
      openCreateEditRewardModal(currentReward)
    } else {
      const reward = await associateLoyaltyProgramReward(rewardId)
      isCancelDissociateReward.value = true
      openCreateEditRewardModal(reward)
    }

    emit('updateReward')
  } finally {
    isSelectExistingRewardModalOpen.value = false
  }
}

const handleRowClicked = (event: any) => {
  if (canUpdateReward) {
    isCancelDissociateReward.value = false
    isAddingFromSelection.value = false
    openCreateEditRewardModal(rewardList.value.find((reward) => reward.id === event.id))
  }
}

const handleSaveReward = async () => {
  emit('updateReward')
  closeCreateEditRewardModal()
}

const closeCreateEditRewardModal = () => {
  if (isCancelDissociateReward.value) {
    isCancelDissociateReward.value = false
    emit('updateReward')
  }

  isCreateEditRewardModalOpen.value = false
  isAddingFromSelection.value = false
  selectedReward.value = null
}

const openCreateEditRewardModal = (reward: LoyaltyReward) => {
  if (reward) {
    selectedReward.value = {
      ...reward,
      includedSegmentIds: reward.includedSegments?.map((segment) => segment.id) || [],
      excludedSegmentIds: reward.excludedSegments?.map((segment) => segment.id) || []
    }

    isCreateEditRewardModalOpen.value = true
  }
}

const getAllCustomerLists = async () => {
  const { data } = await axios.get(`/merchants/${user.value.merchant.id}/segments`, {
    params: {
      page: 1,
      pageSize: 1000
    }
  })

  allCustomerLists.value = data.data
}

onMounted(async () => {
  if (route.query?.openModal) {
    isCreateEditRewardModalOpen.value = true
    await router.replace({ path: route.path, query: { ...route.query, openModal: undefined } })
  }

  // If the merchant has all partners with segmentation support => Toobi / Progmag / Dishop / Bakeronline
  if (hasAllPartnersWithSegmentationSupport.value) getAllCustomerLists()
})

defineExpose({ openSelectExistingRewardModal })
</script>

<template>
  <div>
    <AppTable
      :columns="tableColumns"
      :rows="tableRows"
      :row-classes="canUpdateReward ? 'cursor-pointer' : ''"
      :pagination="pagination && pagination.lastPage > 1 ? pagination : null"
      :is-loading="isLoading"
      @row-clicked="handleRowClicked"
      @update:change-page="(p: number) => emit('update:change-page', p)"
    >
      <template #td-image="{ data }">
        <div>
          <img
            v-if="data?.reward?.pictureUrl"
            :src="data.reward.pictureUrl"
            :alt="data.reward.name || 'Reward image'"
            class="h-10 w-16 min-w-16 rounded-lg object-cover object-center"
          />
          <div v-else class="h-10 w-16 min-w-16 rounded-lg border flex items-center justify-center">
            <BaseIcon name="HeroGiftIconOutline" class="w-5 h-5 text-muted-foreground" />
          </div>
        </div>
      </template>

      <template #td-type="{ data }">
        <RewardTypeTag :reward-type="data?.reward?.rewardType" />
      </template>

      <template #td-name="{ data }">
        <div class="text-sm font-medium text-foreground">
          {{ data?.name || '-' }}
        </div>
      </template>

      <template #td-points="{ data }">{{ formatNumber(data?.points) }}</template>

      <template #td-segments="{ data }">
        <RewardAudienceTag :excluded-audiences="data.excludedSegments" :included-audiences="data.includedSegments" />
      </template>
      <template #td-stores="{ data }">
        <StoreTooltip :stores="data.stores" :available-store-ids="storesWithPermission.map((store) => store.id)" />
      </template>
    </AppTable>

    <LoyaltyRewardModal
      v-if="allCustomerLists && isCreateEditRewardModalOpen && selectedReward"
      v-model:selected-reward="selectedReward"
      :has-all-partners-with-segmentation-support="hasAllPartnersWithSegmentationSupport"
      :all-customer-lists="allCustomerLists"
      :has-compatible-voucher-integration="hasCompatibleVoucherIntegration"
      :dissociate-on-cancel="isCancelDissociateReward"
      :is-add-mode="isAddingFromSelection"
      @close="closeCreateEditRewardModal"
      @selected-reward:saved="handleSaveReward"
    />

    <RewardSelectionModal
      :rewards="existingRewards"
      :is-open="isSelectExistingRewardModalOpen"
      :show-edit-button="false"
      @close-modal="closeSelectExistingRewardModal"
      @create-reward="handleCreateNewFromSelection"
      @select-reward="handleSelectExistingReward"
    />
  </div>
</template>
